import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { DataElementContext } from '../../../page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import ClientDetection from '@/modules/casino/utils/ClientDetection';
import ExpiringLocalStorage from '@/modules/casino/utils/ExpiringLocalStorage';
import moment from 'moment';

type InstallAppBannerProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
  style: React.CSSProperties | undefined;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const InstallAppBanner = (componentProps: InstallAppBannerProps) => {
  let props = componentProps;

  const enableDebug = false; // Set to true to enable debug logs

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const clientData = ClientDetection.getAllInfo();

  const configEnabled = window.config?.installAppBanner?.enabled === '1' ? true : false;
  const skipCooldown = window.config?.installAppBanner?.skipCooldown === '1' ? true : false;

  const configAndroidEnabled = window.config?.installAppBanner?.androidEnabled === '1' ? true : false;
  const configIosEnabled = window.config?.installAppBanner?.iosEnabled === '1' ? true : false;

  const configShortSupressTime = window.config?.installAppBanner?.shortSupressTime;
  const configLongSupressTime = window.config?.installAppBanner?.longSupressTime;
  const isAndroid = clientData?.os?.name && clientData.os.name === 'Android' ? true : false;
  const isIos = clientData?.os?.name && clientData.os.name === 'iOS' ? true : false;

  const isNotApp = window?.config?.appEnv !== 'wrapper-mobile' ? true : false;

  if (!(configEnabled || (configAndroidEnabled && isAndroid) || (configIosEnabled && isIos) || isNotApp)) {
    enableDebug && console.log('Return NULL', { configEnabled, isAndroid, isIos, isNotApp });
    return null;
  }

  const now = moment();
  const [shouldDisplay, setShouldDisplay] = React.useState<boolean>(false);

  const saveCookies = React.useCallback((fields: any, cooldown?: number) => {
    if (skipCooldown) {
      enableDebug && console.log('InstallAppBanner - SKIP Cookies', { fields, cooldown });

      ExpiringLocalStorage.remove('install-app-banner');
      setShouldDisplay(true);
      return;
    }

    enableDebug && console.log('InstallAppBanner - SAVE Cookies', { fields, cooldown });

    ExpiringLocalStorage.set(
      'install-app-banner',
      JSON.stringify(fields),
      60 * 60 * 24 * (cooldown ?? parseInt(configLongSupressTime, 10) ?? 30),
    ); // Save for 30 days
  }, []);

  const checkTimes = () => {
    if (!(isAndroid || isIos) || !isNotApp) {
      return;
    }

    if (skipCooldown) {
      setShouldDisplay(true);
      return;
    }

    const loadedFields = ExpiringLocalStorage.get('install-app-banner');
    const pFields = loadedFields ? JSON.parse(loadedFields) : null;

    enableDebug &&
      console.log('InstallAppBanner - LOAD Cookies', {
        loadedFields,
        pFields,
        next: moment(pFields?.nextDisplay).format('DD-MM-YYYY'),
      });

    if (!pFields) {
      // no cookies found, set initial state
      enableDebug && console.log('InstallAppBanner - no cookies found, setting initial state');
      saveCookies({
        nextDisplay: now.add(parseInt(configShortSupressTime, 10), 'days').valueOf(),
        wasExtendedShort: false,
        wasExtendedLong: false,
      });
      setShouldDisplay(true);
      return;
    }

    if (pFields && moment(pFields.nextDisplay).isAfter(now) && !pFields.wasExtendedShort) {
      // if nextDisplay is in the future and was not extended short yet, set it to true
      enableDebug && console.log('InstallAppBanner - nextDisplay is in the future, setting wasExtendedShort to true');
      saveCookies({
        ...pFields,
        wasExtendedShort: true,
      });
    }

    if (pFields && moment(pFields.nextDisplay).isBefore(now)) {
      // if nextDisplay is in the past -> diplay or extend
      if (!pFields.wasExtendedShort) {
        // if was not extended short yet, extend it
        enableDebug && console.log('InstallAppBanner - nextDisplay is before now, extending short');
        saveCookies({
          ...pFields,
          nextDisplay: now.add(parseInt(configShortSupressTime, 10), 'days').valueOf(),
          wasExtendedShort: true,
        });

        setShouldDisplay(true);
        return;
      } else {
        // if was extended short once, extend it long
        enableDebug && console.log('InstallAppBanner - nextDisplay is before now, extending long');
        if (!pFields.wasExtendedLong) {
          saveCookies({
            ...pFields,
            wasExtendedShort: true,
            wasExtendedLong: true,
            nextDisplay: now.add(parseInt(configLongSupressTime, 10), 'days').valueOf(),
          });

          setShouldDisplay(true);
          return;
        }
      }
      // if none of the conditions above were met, do not display until cookies expire
    }
  };

  React.useEffect(() => {
    checkTimes();
  }, []);

  const onClose = () => {
    enableDebug && console.log('InstallAppBanner - running onClose');
    if (!skipCooldown) checkTimes();
    setShouldDisplay(false);
  };

  const onGetApp = () => {
    enableDebug && console.log('InstallAppBanner - running onGetApp');
    ExpiringLocalStorage.remove('install-app-banner');
    setShouldDisplay(true);
  };

  const getUrl = () => {
    if (isAndroid && configAndroidEnabled && window.config?.installAppBanner?.androidDownloadUrl)
      return window.config?.installAppBanner?.androidDownloadUrl;
    if (isIos && configIosEnabled && window.config?.installAppBanner?.iosDownloadUrl)
      return window.config?.installAppBanner?.iosDownloadUrl;

    return window.config?.installAppBanner?.downloadUrl ?? '';
  };

  const contextValue = React.useMemo(() => {
    return {
      enabled: configEnabled,
      show: (isAndroid || isIos) && isNotApp,
      shouldDisplay: shouldDisplay,
      onGetApp,
      onClose,
      url: getUrl(),
    };
  }, [configEnabled, isAndroid, isNotApp, shouldDisplay, isIos]);

  enableDebug &&
    console.log('InstallAppBanner contextValue', {
      contextValue,
      config: window.config?.installAppBanner ?? {},
      clientData,
      configEnabled,
      isAndroid,
      isIos,
      isNotApp,
      now,
      shouldDisplay,
      ls: ExpiringLocalStorage.get('install-app-banner'),
    });

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText} style={props.style}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default InstallAppBanner;
